import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { formatViews } from "@/utils/movies";
import EmptyState from "@/components/base/EmptyState";

interface HotRankingProps {
  movies: Movie[];
}

export default function HotRanking({ movies }: HotRankingProps) {
  return (
    <section className="w-full bg-background-100 border-y border-background-200 mt-10 md:mt-14">
      <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-12">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
                热门推荐榜
              </h2>
              <p className="mt-1 text-sm text-foreground-500">
                根据播放热度实时排序，大家都在看
              </p>
            </div>
          </div>
          <Link
            to="/search?q=热门"
            className="inline-flex items-center gap-1 text-sm font-medium text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            全部热榜
            <i className="ri-arrow-right-s-line text-lg"></i>
          </Link>
        </div>

        {movies.length === 0 ? (
          <EmptyState title="暂无内容" description="热榜暂时没有数据，稍后再来看看吧" />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-3">
            {movies.map((movie, index) => (
              <Link
                key={movie.id}
                to={`/detail/${movie.id}`}
                className="group flex items-center gap-4 p-2.5 rounded-lg hover:bg-background-50 cursor-pointer transition-colors animate-fade-in"
              >
                <span
                  className={`w-7 text-center font-heading text-2xl font-black shrink-0 ${
                    index < 3 ? "text-primary-500" : "text-foreground-300"
                  }`}
                >
                  {index + 1}
                </span>
                <div className="w-14 h-20 rounded-md overflow-hidden bg-background-200 shrink-0">
                  <img
                    src={movie.cover}
                    alt={`${movie.title} 海报`}
                    title={movie.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-accent-600 transition-colors">
                    {movie.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500 line-clamp-1">
                    {movie.typeName} · {movie.region} · {movie.year}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500 line-clamp-1">
                    {movie.genres.slice(0, 3).join(" / ")}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <span className="flex items-center gap-1 justify-end text-sm font-bold text-primary-600">
                    <i className="ri-star-fill text-xs"></i>
                    {movie.rating.toFixed(1)}
                  </span>
                  <span className="mt-1 block text-xs text-foreground-400">
                    {formatViews(movie.views)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}